import type { Inline } from "./questionDocument";

/**
 * Mention links: the portable form of an atom, used by the clipboard's HTML
 * flavour (see promptClipboardHtml).
 *
 * A mention becomes `<a href="https://…/mention?mention=…">@handle</a>`, whose
 * query string is the mention's own JSON payload, key for key. Hosts that accept
 * pasted rich text (Slack, Gmail, Docs) keep an anchor's `href` but discard
 * unknown elements, classes and `data-*` attributes, so the URL is the only
 * place a payload survives being pasted into another app and copied back out.
 *
 * Because the query keys are the JSON keys, encoding is a copy of the payload
 * and decoding hands the params straight to the parsers in
 * promptMentionSerialization — there is no third vocabulary to keep in step.
 *
 * The link is also a real destination: `/mention` resolves the id and redirects
 * to the AskPage with that mention pre-filled, so clicking it in Slack does
 * something useful.
 */

const MENTION_PATH = "/mention";

/**
 * Hosts whose `/mention` links we decode. Anything else is treated as an
 * ordinary link, so unrelated pasted HTML can never inject an atom. Dev
 * hostnames vary per machine, so the current origin is always trusted too.
 */
const TRUSTED_HOSTS = [
	"askgvt.dev",
	"gvtlabs.dev",
	"askgvt.local",
	"gvtlabs.local",
];

/**
 * Public-id prefixes a mention may reference. An atom carrying anything else
 * (notably a `vm_…` ephemeral video display id, which is meaningless outside the
 * session that minted it) gets no link, since the URL could never resolve.
 */
const PUBLIC_ID_RE = /^(sa|in|vi)_[A-Za-z0-9]+$/;

const isTrustedHost = (host: string): boolean =>
	host === window.location.host ||
	TRUSTED_HOSTS.some(
		(trusted) => host === trusted || host.endsWith(`.${trusted}`),
	);

/**
 * The mention payload for `inline` as `[key, value]` pairs, or null when the
 * atom cannot be addressed by a URL.
 */
function payloadEntries(inline: Inline): [string, string][] | null {
	if (inline.kind === "text") return null;

	if (inline.kind === "mention") {
		const { type, handle, platform, id } = inline.data;
		if (id === undefined || !PUBLIC_ID_RE.test(id)) return null;
		const entries: [string, string][] = [
			["mention", type],
			["handle", handle],
		];
		if (platform) entries.push(["platform", platform]);
		entries.push(["id", id]);
		return entries;
	}

	const { id, label, startTimeSec, endTimeSec } = inline.data;
	if (!PUBLIC_ID_RE.test(id)) return null;
	const entries: [string, string][] = [
		["mention", "video"],
		["id", id],
	];
	if (startTimeSec != null)
		entries.push(["start_time_sec", String(startTimeSec)]);
	if (endTimeSec != null) entries.push(["end_time_sec", String(endTimeSec)]);
	if (label) entries.push(["title", label]);
	return entries;
}

/** The `/mention` URL for an atom, or null when it has no addressable id. */
export function mentionUrl(inline: Inline): string | null {
	const entries = payloadEntries(inline);
	if (!entries) return null;
	const url = new URL(MENTION_PATH, window.location.origin);
	for (const [key, value] of entries) url.searchParams.set(key, value);
	return url.toString();
}

/** Params whose JSON counterpart is a number rather than a string. */
const NUMERIC_KEYS = ["start_time_sec", "end_time_sec"];

/**
 * The mention payload carried by `href`, or null when it is not one of our
 * mention links. The result is the JSON equivalent of the query string, so
 * callers can pass it to `mentionFromPayload` exactly like a parsed inline JSON
 * block.
 */
export function mentionPayloadFromUrl(
	href: string,
): Record<string, unknown> | null {
	let url: URL;
	try {
		url = new URL(href, window.location.origin);
	} catch {
		return null;
	}
	if (url.pathname !== MENTION_PATH || !isTrustedHost(url.host)) return null;
	const payload: Record<string, unknown> = {};
	for (const [key, value] of url.searchParams) {
		if (NUMERIC_KEYS.includes(key)) {
			const asNumber = Number(value);
			if (Number.isFinite(asNumber)) payload[key] = asNumber;
			continue;
		}
		payload[key] = value;
	}
	return payload.mention !== undefined ? payload : null;
}
